// Modern polyphonic notification sounds via Web Audio API.
// Presets designed to feel like UPI success / banking app confirms / Discord pings — no external assets.

const KEY = "ldc_sound_enabled";

export function isSoundEnabled() {
  const v = localStorage.getItem(KEY);
  if (v === null) return true;
  return v === "1";
}

export function setSoundEnabled(enabled) {
  localStorage.setItem(KEY, enabled ? "1" : "0");
}

let _ctx = null;
function ctx() {
  if (typeof window === "undefined") return null;
  if (!_ctx) {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      _ctx = AC ? new AC() : null;
    } catch (e) {
      console.warn("[sound] AudioContext unavailable", e);
    }
  }
  return _ctx;
}

function envelope(g, t0, attack, sustain, release, peak) {
  g.gain.cancelScheduledValues(t0);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(peak, t0 + attack);
  g.gain.setValueAtTime(peak, t0 + attack + sustain);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + sustain + release);
}

function playNote({ freq, when = 0, duration = 0.24, type = "sine", peak = 0.13, attack = 0.008, release = 0.16 }) {
  const c = ctx();
  if (!c) return;
  try {
    if (c.state === "suspended") c.resume();
    const t0 = c.currentTime + when;
    const osc = c.createOscillator();
    const g = c.createGain();
    // Subtle warmth: send through a soft lowpass filter
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 4500;
    lp.Q.value = 0.6;
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    osc.connect(g).connect(lp).connect(c.destination);
    envelope(g, t0, attack, Math.max(0, duration - attack - release), release, peak);
    osc.start(t0);
    osc.stop(t0 + duration + release + 0.05);
  } catch (e) {
    console.warn("[sound] play failed", e);
  }
}

// Play multiple notes together for a chord
function playChord({ freqs, when = 0, duration = 0.28, type = "sine", peak = 0.08 }) {
  freqs.forEach((f) => playNote({ freq: f, when, duration, type, peak, attack: 0.01, release: 0.18 }));
}

function seq(steps, mixIn = false) {
  if (!isSoundEnabled()) return;
  let t = mixIn ? 0 : 0;
  for (const s of steps) {
    if (s.chord) {
      playChord({ ...s, when: t });
    } else {
      playNote({ ...s, when: t });
    }
    t += (s.gap ?? (s.duration || 0.18));
  }
}

// ---------- Presets ----------
export const sounds = {
  // Sparkling UPI-like success: rising major triad + soft ping
  payment: () => seq([
    { chord: true, freqs: [523.25, 659.25, 783.99], duration: 0.20, peak: 0.06, type: "sine", gap: 0.08 },   // C E G
    { chord: true, freqs: [659.25, 783.99, 987.77], duration: 0.24, peak: 0.07, type: "sine", gap: 0.10 },   // E G B
    { chord: true, freqs: [783.99, 987.77, 1174.66], duration: 0.36, peak: 0.08, type: "sine", gap: 0.20 },  // G B D
    { freq: 1567.98, duration: 0.10, type: "triangle", peak: 0.05, gap: 0.10 },                              // sparkle
  ]),

  // Banking-app "deposit approved" — 2 gentle bells
  success: () => seq([
    { freq: 880, duration: 0.16, type: "sine", peak: 0.09, gap: 0.10 },
    { freq: 1174.66, duration: 0.30, type: "sine", peak: 0.10, gap: 0.32 },
  ]),

  // Discord-style notification ping (double note)
  info: () => seq([
    { freq: 880, duration: 0.14, type: "sine", peak: 0.07, gap: 0.10 },
    { freq: 660, duration: 0.20, type: "sine", peak: 0.07, gap: 0.22 },
  ]),

  // Refund completed — descending soft chime
  refund: () => seq([
    { freq: 987.77, duration: 0.18, type: "triangle", peak: 0.07, gap: 0.10 },
    { freq: 783.99, duration: 0.22, type: "triangle", peak: 0.07, gap: 0.14 },
    { freq: 659.25, duration: 0.30, type: "sine", peak: 0.07, gap: 0.30 },
  ]),

  // Deposit approved — modern warm chord + tail
  deposit: () => seq([
    { chord: true, freqs: [440, 554.37, 659.25], duration: 0.32, peak: 0.08, type: "sine", gap: 0.20 },      // A C# E
    { freq: 880, duration: 0.32, type: "sine", peak: 0.07, gap: 0.34 },
  ]),

  linkCreated: () => seq([
    { freq: 987.77, duration: 0.10, type: "sine", peak: 0.05, gap: 0.08 },
    { freq: 1318.51, duration: 0.14, type: "sine", peak: 0.05, gap: 0.14 },
  ]),

  failure: () => seq([
    { freq: 415.30, duration: 0.16, type: "sawtooth", peak: 0.05, gap: 0.14 },
    { freq: 311.13, duration: 0.28, type: "sawtooth", peak: 0.05, gap: 0.30 },
  ]),
};
